import { useRef, useCallback } from 'react';
import { getLogger } from '@confluence/logger';
import { useMutation } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { CreateContentMentionNotificationAction } from '../graphql/CreateContentMentionNotificationAction.graphql';
import type { NotificationAction } from '../graphql/__types__/CreateContentMentionNotificationAction';

type MentionNotificationMutationProps = {
	contentId: string;
};

type SendCreateMentionNotificationProps = {
	displayName: string;
	mentions: {
		mentionLocalId: string;
		mentionedUserAccountId: string;
		notificationAction: NotificationAction;
	}[];
};

export const useMentionNotificationMutation = ({ contentId }: MentionNotificationMutationProps) => {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>();
	const [createContentMentionNotificationAction] = useMutation(
		CreateContentMentionNotificationAction,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const logger = getLogger('mentions-notifications');

	const sendMentionNotification = useCallback(
		({ mentions }: SendCreateMentionNotificationProps) => {
			const fireAnalyticsEvent = (action: 'succeeded' | 'failed') => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'mentionsNotifications',
						action,
						actionSubject: 'mentionInputMutation',
						attributes: {
							//TODO: confirm if we need to pass in mention IDs / local IDs here
							contentId,
						},
					},
				}).fire();
			};

			// NCS draft syncs need to happen prior to mutation in order for
			// email content to show up. A 15 second buffer after user closes
			// the flag notification will give draft syncs a chance to happen.
			timerRef.current = setTimeout(() => {
				// tells backend to send notificiation for the list of mention IDs
				createContentMentionNotificationAction({
					variables: {
						contentId,
						mentions,
					},
				})
					.then(() => {
						fireAnalyticsEvent('succeeded');
					})
					.catch(() => {
						//TODO: create new error flag that handles multiple names
						fireAnalyticsEvent('failed');
					})
					.finally(() => {
						timerRef.current = null;
					});
			}, 15000);
		},
		[contentId, createAnalyticsEvent, createContentMentionNotificationAction],
	);

	const sendCancelMentionNotification = useCallback(
		({ mentions }: SendCreateMentionNotificationProps) => {
			createContentMentionNotificationAction({
				variables: {
					contentId,
					mentions,
				},
			}).catch((error) => {
				//TODO: Handle error case with error flag
				logger.error(error);
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentId, createContentMentionNotificationAction],
	);

	return {
		sendMentionNotification,
		sendCancelMentionNotification,
	};
};
